import {
  CustomFieldType,
  IDataFieldValue,
} from "@shared/lib/types/briefInfluencerV2";
import { SelectButton } from "@shared/ui/Select";
import { Button, Input, InputNumber } from "antd";
import { X as Close, Plus, UploadCloud } from "lucide-react";
import styles from "../styles.module.scss";

interface BriefCustomFieldsFormProps {
  fieldName: string;
  fieldType: CustomFieldType;
  fieldDefault: IDataFieldValue | undefined;
  dropdownOptions: string[];
  isEditing: boolean;
  isSubmitting: boolean;
  hideActions?: boolean;
  formId?: string;
  onFieldNameChange: (value: string) => void;
  onFieldTypeChange: (type: CustomFieldType) => void;
  onFieldDefaultChange: (value: string | number | string[] | undefined) => void;
  onDropdownOptionsChange: (options: string[]) => void;
  onAddOption: () => void;
  onRemoveOption: (index: number) => void;
  onOptionChange: (index: number, value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
}

export function BriefCustomFieldsForm({
  fieldName,
  fieldType,
  fieldDefault,
  dropdownOptions,
  isEditing,
  isSubmitting,
  hideActions = false,
  formId,
  onFieldNameChange,
  onFieldTypeChange,
  onFieldDefaultChange,
  onDropdownOptionsChange,
  onAddOption,
  onRemoveOption,
  onOptionChange,
  onSubmit,
  onCancel,
}: BriefCustomFieldsFormProps) {
  // Get dropdown options for SelectButton
  const dropdownSelectOptions = dropdownOptions
    .filter((o) => o.trim())
    .map((opt) => ({ label: opt, value: opt }));

  // Get current default value for dropdown (multiSelect)
  const dropdownDefaultValue = Array.isArray(fieldDefault)
    ? fieldDefault
    : fieldDefault
      ? [String(fieldDefault)]
      : [];

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isEditing) {
      onSubmit(e);
    }
  };

  return (
    <form
      id={formId}
      onSubmit={handleFormSubmit}
      className={`${styles.form} ${hideActions ? styles.formInlineBody : ""}`}
    >
      <div className={styles.formGroup}>
        <label className={styles.label}>Field Name</label>
        <Input
          type="text"
          value={fieldName}
          onChange={(e) => onFieldNameChange(e.target.value)}
          placeholder="e.g. Shipping Status"
          className={styles.input}
          autoFocus={!isEditing}
          required
          disabled={isEditing}
          size="small"
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>Data Type</label>
        <div className={styles.typeGrid}>
          <button
            type="button"
            onClick={() => onFieldTypeChange(CustomFieldType.STRING)}
            disabled={isEditing}
            className={`${styles.typeButton} ${
              fieldType === "string" ? styles.typeButtonActive : ""
            } ${isEditing ? styles.typeButtonDisabled : ""}`}
          >
            Text
          </button>
          <button
            type="button"
            onClick={() => onFieldTypeChange(CustomFieldType.DROPDOWN)}
            disabled={isEditing}
            className={`${styles.typeButton} ${
              fieldType === CustomFieldType.DROPDOWN
                ? styles.typeButtonActive
                : ""
            } ${isEditing ? styles.typeButtonDisabled : ""}`}
          >
            Dropdown
          </button>
          <button
            type="button"
            onClick={() => onFieldTypeChange(CustomFieldType.NUMBER)}
            disabled={isEditing}
            className={`${styles.typeButton} ${
              fieldType === "number" ? styles.typeButtonActive : ""
            } ${isEditing ? styles.typeButtonDisabled : ""}`}
          >
            Number
          </button>
          <button
            type="button"
            onClick={() => onFieldTypeChange(CustomFieldType.UPLOAD)}
            disabled={isEditing}
            className={`${styles.typeButton} ${
              fieldType === CustomFieldType.UPLOAD
                ? styles.typeButtonActive
                : ""
            } ${isEditing ? styles.typeButtonDisabled : ""}`}
          >
            Upload
          </button>
        </div>
      </div>

      {fieldType === CustomFieldType.DROPDOWN && (
        <div className={styles.dropdownOptionsContainer}>
          <label className={styles.dropdownOptionsLabel}>Options</label>
          {dropdownOptions.map((option, index) => (
            <div key={index} className={styles.optionRow}>
              <Input
                type="text"
                value={option}
                onChange={(e) => onOptionChange(index, e.target.value)}
                placeholder={`Option ${index + 1}`}
                className={styles.optionInput}
                required={index === 0}
                disabled={isEditing}
                size="small"
              />
              {dropdownOptions.length > 1 && !isEditing && (
                <button
                  type="button"
                  onClick={() => onRemoveOption(index)}
                  className={styles.removeOptionButton}
                >
                  <Close size={14} />
                </button>
              )}
            </div>
          ))}
          {!isEditing && (
            <button
              type="button"
              onClick={onAddOption}
              className={styles.addOptionButton}
            >
              <Plus size={12} /> Add Option
            </button>
          )}
        </div>
      )}

      {fieldType !== CustomFieldType.UPLOAD && (
        <div className={styles.formGroup}>
          <label className={styles.label}>Default Value (Optional)</label>
          {fieldType === CustomFieldType.DROPDOWN ? (
            <SelectButton
              mode="multiple"
              value={dropdownDefaultValue}
              onChange={(value) => {
                onFieldDefaultChange(Array.isArray(value) ? value : []);
              }}
              options={dropdownSelectOptions}
              placeholder="Select default values"
              className={styles.selectInput}
              disabled={isEditing}
              withFooterActions
            />
          ) : fieldType === CustomFieldType.NUMBER ? (
            <InputNumber
              value={
                typeof fieldDefault === "number"
                  ? fieldDefault
                  : fieldDefault
                    ? Number(fieldDefault)
                    : undefined
              }
              onChange={(value) => {
                onFieldDefaultChange(value || undefined);
              }}
              placeholder="Value for existing rows"
              className={styles.inputNumber}
              disabled={isEditing}
              size="small"
              type="number"
              // classNames={{ input: styles.inputNumberInput }}
            />
          ) : (
            <Input
              type="text"
              value={String(fieldDefault || "")}
              onChange={(e) => onFieldDefaultChange(e.target.value)}
              placeholder="Value for existing rows"
              className={styles.input}
              disabled={isEditing}
            />
          )}
        </div>
      )}

      {fieldType === CustomFieldType.UPLOAD && (
        <div className={styles.uploadInfo}>
          <UploadCloud size={16} className={styles.uploadIcon} />
          <p>
            Upload fields allow you to attach files or links to each creator.
            Multiple items are supported.
          </p>
        </div>
      )}

      {!hideActions ? (
        <div className={styles.formActions}>
          {isEditing ? (
            <Button onClick={onCancel} className={styles.backButton}>
              Back
            </Button>
          ) : (
            <>
              <Button onClick={onCancel} className={styles.cancelButton}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={isSubmitting}
                className={styles.submitButton}
              >
                Create Field
              </Button>
            </>
          )}
        </div>
      ) : null}
    </form>
  );
}
